'use client';
import {
  AlertBanner,
  AlertBannerBadge,
  AlertBannerBadgeLabel,
  AlertBannerBadgeVariant,
  AlertBannerBody,
  AlertBannerDismiss,
  AlertBannerGroundVariant,
  AlertBannerMessage,
  AlertBannerText,
  AlertBannerTextVariant,
  alertBannerGroundVariants,
} from '@vs-shop/commerce-ui/alertBanner';
import { Container } from '@vs-shop/ui/container';
import { IconTouchable } from '@vs-shop/ui/iconTouchable';
import { clsxm } from '@vs-shop/utils';
import { getLucideIconFromKebabString } from '../../../../../shared/utils/lucideIcons';
import { Option } from 'effect';
import { ChevronRight, X } from 'lucide-react';
import { createElement } from 'react';
import { useIntl } from 'react-intl';

import Link from '@vs-shop/image-link/Link';

import type { Announcement } from '@vs-shop/engine/service/announcements/Announcement';
import { useAnnouncement } from '@vs-shop/headless/announcements/useAnnouncement';

type AlertMessageType = Announcement['type'];

type Props = {
  alertMessages: readonly Announcement[];
};

const GROUND_VARIANT_BY_TYPE = {
  base: AlertBannerGroundVariant.BASE,
  warning: AlertBannerGroundVariant.WARNING,
  error: AlertBannerGroundVariant.DESTRUCTIVE,
  success: AlertBannerGroundVariant.SUCCESS,
} satisfies Record<AlertMessageType, AlertBannerGroundVariant>;

const BADGE_VARIANT_BY_TYPE = {
  base: AlertBannerBadgeVariant.UNSTYLED,
  warning: AlertBannerBadgeVariant.WARNING,
  error: AlertBannerBadgeVariant.DESTRUCTIVE,
  success: AlertBannerBadgeVariant.SUCCESS,
} satisfies Record<AlertMessageType, AlertBannerBadgeVariant>;

export function AlertMessagesBanner({ alertMessages }: Props) {
  const { message, dismiss } = useAnnouncement('banner', alertMessages);
  const intl = useIntl();
  if (Option.isNone(message)) return null;
  const currentMessage = Option.getOrThrow(message);
  const icon = currentMessage.icon;
  const title = currentMessage.title ?? null;
  const messageText = currentMessage.message ?? null;
  const type = currentMessage.type;
  const link = currentMessage.link ?? null;
  const buttonText = currentMessage.buttonText ?? null;

  const IconComponent = icon !== null ? getLucideIconFromKebabString(icon) : null;

  const ground = alertBannerGroundVariants({ variant: GROUND_VARIANT_BY_TYPE[type] });
  const badgeVariant = BADGE_VARIANT_BY_TYPE[type];

  const messageContent = (
    <>
      {IconComponent !== null ? createElement(IconComponent) : null}

      <AlertBannerBody>
        <AlertBannerText
          variant={link !== null ? AlertBannerTextVariant.UNDERLINED : AlertBannerTextVariant.PLAIN}
        >
          {title ? (
            // space instead of margin to keep the underline flow
            <span>{title}: </span>
          ) : null}
          <span>{messageText}</span>
        </AlertBannerText>

        {buttonText !== null ? (
          <AlertBannerBadge variant={badgeVariant}>
            <AlertBannerBadgeLabel>{buttonText}</AlertBannerBadgeLabel>
            <ChevronRight />
          </AlertBannerBadge>
        ) : null}
      </AlertBannerBody>
    </>
  );

  return (
    <Container classNameWrapper={clsxm('border-b border-foreground/10', ground)}>
      <AlertBanner>
        {link !== null ? (
          <AlertBannerMessage render={<Link href={link} />}>{messageContent}</AlertBannerMessage>
        ) : (
          <AlertBannerMessage>{messageContent}</AlertBannerMessage>
        )}

        <AlertBannerDismiss>
          <IconTouchable
            type="button"
            aria-label={intl.formatMessage({
              id: 'alertMessages.dismiss',
              defaultMessage: 'Schovat',
            })}
            onClick={dismiss}
          >
            <X className="size-4.5 shrink-0 stroke-2 lap:size-5" />
          </IconTouchable>
        </AlertBannerDismiss>
      </AlertBanner>
    </Container>
  );
}
